import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

const PrivacyPolicy = () => {
  const updated = "June 18, 2026";
  return (
    <>
      <Helmet>
        <title>Privacy Policy | MyStudio Columbus GA</title>
        <meta name="description" content="Privacy Policy for MyStudio Columbus, GA — how we collect, use, and protect your information." />
        <link rel="canonical" href="https://bookmystudiospace.com/privacy-policy" />
      </Helmet>
      <article className="container mx-auto px-4 lg:px-8 py-16 max-w-3xl">
        <h1 className="font-display text-3xl md:text-4xl tracking-wider mb-2">Privacy Policy</h1>
        <p className="text-xs text-muted-foreground font-body mb-10">Last updated: {updated}</p>

        <div className="prose prose-invert max-w-none font-body text-sm text-muted-foreground space-y-6 [&_h2]:text-foreground [&_h2]:font-display [&_h2]:tracking-wider [&_h2]:text-lg [&_h2]:mt-10 [&_h2]:mb-3 [&_a]:text-primary">
          <p>This page is maintained by MyStudio Columbus, GA ("MyStudio," "we," "us," or "our") to explain how we collect, use, and protect information when you visit <a href="https://bookmystudiospace.com">bookmystudiospace.com</a> or use our services.</p>

          <h2>Information We Collect</h2>
          <p>We collect information you give us directly (such as name, email, phone number, billing address, and booking details when you contact us, book a studio, or purchase from our shop), as well as information collected automatically through cookies and analytics tools (such as IP address, device/browser type, pages viewed, and referral source).</p>

          <h2>How We Use Your Information</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>To process bookings, orders, payments, and refunds</li>
            <li>To communicate with you about your booking, order, or inquiry</li>
            <li>To send marketing emails (only if you opted in — you can unsubscribe anytime)</li>
            <li>To improve our website, services, and customer experience</li>
            <li>To prevent fraud, spam, and abuse</li>
            <li>To comply with legal obligations</li>
          </ul>

          <h2>Cookies & Tracking</h2>
          <p>We use cookies and similar technologies for essential site functionality, analytics, and advertising. Third-party tools currently in use include:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Google Analytics</strong> — for site usage and traffic analytics</li>
            <li><strong>Meta (Facebook) Pixel</strong> — for ad measurement and retargeting</li>
            <li><strong>Google reCAPTCHA Enterprise</strong> — for spam and bot protection on our forms</li>
          </ul>
          <p>See our <Link to="/cookie-policy">Cookie Policy</Link> for details and how to manage your preferences.</p>

          <h2>Payment Processing</h2>
          <p>Payments are processed by Stripe. We do not store full credit card numbers on our servers. Stripe's privacy practices are described at <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">stripe.com/privacy</a>.</p>

          <h2>Sharing Your Information</h2>
          <p>We do not sell your personal information. We share information only with service providers necessary to operate our business (e.g., payment processor, email delivery, hosting, analytics) and where required by law.</p>

          <h2>Data Retention</h2>
          <p>We retain personal information as long as needed to provide our services, comply with legal obligations, resolve disputes, and enforce our agreements.</p>

          <h2>Your Rights</h2>
          <p>Depending on your location, you may have rights to access, correct, delete, or export your personal information, or to opt out of certain processing. To exercise these rights, email us at <a href="mailto:services@bookmystudiospace.com">services@bookmystudiospace.com</a>.</p>

          <h2>Children's Privacy</h2>
          <p>Our services are not directed to children under 13, and we do not knowingly collect personal information from children under 13.</p>

          <h2>Changes to This Policy</h2>
          <p>We may update this Privacy Policy from time to time. Material changes will be posted on this page with an updated "Last updated" date.</p>

          <h2>Contact</h2>
          <p>MyStudio Columbus, GA<br/>2300 Whittlesey Rd Ste E, Columbus, GA 31909<br/>Email: <a href="mailto:services@bookmystudiospace.com">services@bookmystudiospace.com</a><br/>Phone: <a href="tel:7064787640">706.478.7640</a></p>
        </div>
      </article>
    </>
  );
};

export default PrivacyPolicy;